import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { PaginatedResponse, TaskTemplate, TaskDetail } from "@/lib/types"

export function useTaskTemplates() {
  return useQuery({
    queryKey: ["task-templates"],
    queryFn: () => api.get<PaginatedResponse<TaskTemplate>>("/api/v1/task_templates"),
  })
}

export function useTaskTemplate(id?: string) {
  return useQuery({
    queryKey: ["task-templates", id],
    queryFn: () => api.get<{ data: TaskTemplate }>(`/api/v1/task_templates/${id}`),
    enabled: !!id,
  })
}

export function useCreateTaskTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: TaskTemplate }>("/api/v1/task_templates", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task-templates"] })
    },
  })
}

export function useUpdateTaskTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: TaskTemplate }>(`/api/v1/task_templates/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task-templates"] })
    },
  })
}

export function useDeleteTaskTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/task_templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task-templates"] })
    },
  })
}

export function useCreateTaskFromTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data?: Record<string, unknown> }) =>
      api.post<{ data: TaskDetail }>(`/api/v1/task_templates/${templateId}/create_task`, data || {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}
